"use client";

// Admin control for the app-wide display currency (persisted in AppSetting).

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/lib/api-client";
import { setAppCurrency } from "@/components/booking/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n/provider";

const CURRENCIES = [
  "EUR", "USD", "GBP", "CHF", "CAD", "AUD", "NZD", "SEK", "NOK", "DKK", "PLN", "JPY",
];

export function CurrencySetting() {
  const qc = useQueryClient();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["settings"],
    queryFn: () => apiGet<{ currency: string }>("/api/settings"),
  });
  const currency = data?.currency ?? "EUR";

  const change = async (v: string) => {
    setBusy(true);
    try {
      await apiPut("/api/settings", { currency: v });
      setAppCurrency(v);
      toast.success(t("settings.currencySaved", { currency: v }));
      qc.invalidateQueries({ queryKey: ["settings"] });
      // Prices are rendered in many places; a reload guarantees a clean sweep.
      setTimeout(() => window.location.reload(), 600);
    } catch (err: any) {
      toast.error(err.message || "Could not change currency");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Select value={currency} onValueChange={change} disabled={busy}>
      <SelectTrigger className="w-[130px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CURRENCIES.map((c) => (
          <SelectItem key={c} value={c}>
            {c}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
